'use client';

import { useTranslations } from 'next-intl';
import { CheckCircle2, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';

interface CompletionScreenProps {
  email: string;
  donationId: string | null;
  tenantSlug: string | null;
}

/** Registration success screen. Lifted from dgr/register with semantic tokens. */
export function CompletionScreen({ email, donationId, tenantSlug }: CompletionScreenProps) {
  const t = useTranslations('register');
  const tDonate = useTranslations('donate');

  const { data: donationStatus } = trpc.publicDonation.getStatus.useQuery(
    { donationId: donationId ?? '' },
    { enabled: Boolean(donationId), staleTime: Infinity },
  );
  const receiptAvailable = donationStatus?.taxReceiptAvailable ?? false;

  return (
    <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-6">
      <div className="flex flex-col items-center gap-4 py-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/20">
          <CheckCircle2 className="h-7 w-7 text-emerald-600" />
        </div>
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">{t('registrationCompleteTitle')}</h1>
          <p className="text-muted-foreground">
            {t('registrationCompleteDescription', {
              email,
            })}
          </p>
        </div>
        <div className="flex justify-center gap-3">
          {receiptAvailable && tenantSlug && donationId && (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                window.open(`/api/receipts/${tenantSlug}/donations/${donationId}`, '_blank')
              }
            >
              <FileDown className="mr-2 h-4 w-4" />
              {tDonate('form.downloadReceipt')}
            </Button>
          )}
          <Button
            type="button"
            onClick={() => window.location.assign('/my/dashboard')}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {t('goToDashboard')}
          </Button>
        </div>
      </div>
    </div>
  );
}
